import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';

const LEGACY_ASSET_PILL_PREFIX = 'asset-';
export const LEGACY_COMMENT_PILL_PREFIX = 'comment-';

const stripLegacyPillPrefix = (monikerId: string, prefix: string): string | undefined => {
  if (!monikerId.startsWith(prefix)) {
    return undefined;
  }

  const stripped = monikerId.slice(prefix.length);

  return stripped.length > 0 ? stripped : undefined;
};

/**
 * Resolves a moniker id against message pills. Rendering branches on `pill.type`, not id shape.
 * Lookup accepts the pill `id`, legacy prefixed monikers from early get_status passes, and entity
 * ids (`versionId`, `assetId`, `boardId`, `commentId`) when the moniker cites those directly.
 * `versionId` is checked before `assetId` so a version citation is not swallowed by an asset pill
 * whose `assetId` was filled with that same version id.
 */
export const findWorkspaceAgentMessagePill = (
  pills: readonly WorkspaceAgentMessagePill[],
  monikerId: string,
): WorkspaceAgentMessagePill | undefined => {
  const exact = pills.find((pill) => pill.id === monikerId);

  if (exact) {
    return exact;
  }

  const legacyAssetId = stripLegacyPillPrefix(monikerId, LEGACY_ASSET_PILL_PREFIX);

  if (legacyAssetId) {
    const legacyAsset = pills.find((pill) => pill.type === 'asset' && pill.id === legacyAssetId);

    if (legacyAsset) {
      return legacyAsset;
    }
  }

  const legacyCommentId = stripLegacyPillPrefix(monikerId, LEGACY_COMMENT_PILL_PREFIX);

  if (legacyCommentId) {
    const legacyComment = pills.find((pill) => pill.type === 'comment' && pill.id === legacyCommentId);

    if (legacyComment) {
      return legacyComment;
    }
  }

  const assetByVersionId = pills.find((pill) => pill.type === 'asset' && pill.versionId === monikerId);

  if (assetByVersionId) {
    return assetByVersionId;
  }

  const assetByAssetId = pills.find((pill) => pill.type === 'asset' && pill.assetId === monikerId);

  if (assetByAssetId) {
    return assetByAssetId;
  }

  const boardByBoardId = pills.find((pill) => pill.type === 'board' && pill.boardId === monikerId);

  if (boardByBoardId) {
    return boardByBoardId;
  }

  return pills.find((pill) => pill.type === 'comment' && pill.commentId === monikerId);
};

/** Monikers that predate wire pill metadata but are known to be comment cards. */
export const isUnresolvedLegacyCommentMoniker = (
  monikerId: string,
  pill: WorkspaceAgentMessagePill | undefined,
): boolean => pill === undefined && monikerId.startsWith(LEGACY_COMMENT_PILL_PREFIX);
